import React, {useId} from "react";
import {
  Box,
  Flex,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
} from "@chakra-ui/react";
import {LANGUAGE_CONFIGURATION_BOUNDS} from "../../models/constants/constants.ts";
import type {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";

interface LanguageConfigurationProps {
  readonly config: Partial<ILanguageConfiguration>;
  readonly setConfig: (config: Partial<ILanguageConfiguration>) => void;
  readonly isDisabled?: boolean;
  readonly action?: React.ReactNode;
}

const CONFIGURATION_FIELDS = [
  {key: "maxCharactersPerLine", label: "Max Characters per line"},
  {key: "minFramesGap", label: "Min frame gap"},
  {key: "maxSubtitleTime", label: "Max duration in seconds"},
  {key: "maxLines", label: "Max lines"},
  {key: "minSubtitleTimeInFrames", label: "Min duration in frames"},
] as const satisfies readonly {
  readonly key: keyof ILanguageConfiguration;
  readonly label: string;
}[];

const LanguageConfiguration: React.FC<LanguageConfigurationProps> = (
  {
    config,
    setConfig,
    isDisabled,
    action,
  }: LanguageConfigurationProps) => {
  const baseId: string = useId();

  return (
    <Flex flexDir="column" gap={1} position="relative">
      {action && <Box position="absolute" right={0} top={0}>{action}</Box>}
      {CONFIGURATION_FIELDS.map(({key, label}) => {
        const value: number | undefined = config[key];
        if (value === undefined) return null;

        const bounds: {min: number; max: number} = LANGUAGE_CONFIGURATION_BOUNDS[key];
        const labelId: string = `${baseId}-${key}-label`;

        return (
          <Box key={key} mb={2}>
            <Text id={labelId} color="white" fontSize="sm" mb={1}>{label}</Text>
            <Flex alignItems="center">
              <NumberInput
                min={bounds.min}
                max={bounds.max}
                maxW="180px"
                mr="2rem"
                value={value}
                onChange={(next: string) => setConfig({[key]: Number(next)})}
                isDisabled={isDisabled}
              >
                <NumberInputField
                  aria-labelledby={labelId}
                  sx={{
                    border: "none",
                    outline: "none",
                    boxShadow: "0 0 0 0.5px gray",
                    color: "white",
                    fontVariantNumeric: "tabular-nums",
                    _focus: {boxShadow: "0 0 0 1px var(--chakra-colors-brandPurple-500)"},
                    _hover: {boxShadow: "0 0 0 1px var(--chakra-colors-brandPurple-500)"},
                  }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper color="white"/>
                  <NumberDecrementStepper color="white"/>
                </NumberInputStepper>
              </NumberInput>
              <Box flex="1" pr="24px">
                <Slider
                  aria-labelledby={labelId}
                  min={bounds.min}
                  max={bounds.max}
                  focusThumbOnChange={false}
                  value={value}
                  onChange={(next: number) => setConfig({[key]: next})}
                  isDisabled={isDisabled}
                >
                  <SliderTrack bg="white">
                    <SliderFilledTrack bg="brandPurple.500"/>
                  </SliderTrack>
                  <SliderThumb
                    fontSize="sm"
                    boxSize="32px"
                    bg="white"
                    color="black"
                    border="2px solid"
                    borderColor="brandPurple.500"
                    sx={{fontVariantNumeric: "tabular-nums"}}
                  >
                    {value}
                  </SliderThumb>
                </Slider>
              </Box>
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
};

export default LanguageConfiguration;
